import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, MapPinIcon, ClockIcon, DownloadIcon, CheckCircleIcon } from 'lucide-react';
const REGISTRATION_URL = 'https://forms.gle/3G1EHzDfXK7McZ1r6';
import { useLanguage } from '../contexts/LanguageContext';
const FLYER_IMAGE = 'https://cdn.magicpatterns.com/uploads/aY9UUKtppzxeKonAnhnxgN/Save_the_Date_HAA.jpg';
export function SaveTheDatePage() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const t = {
    back: isEs ? 'Volver al sitio' : 'Back to site',
    eyebrow: isEs ? 'Voces de Impacto · Reserva la fecha' : 'Voices of Impact · Save the Date',
    title: isEs ? 'Una velada para conectar, empoderar y celebrar' : 'An evening to connect, empower, and celebrate',
    subtitle: isEs ? 'Acompáñanos en una recepción privada de cóctel honrando a los periodistas, narradores y agentes de cambio cuyo trabajo eleva las voces latinas y fortalece a las comunidades en todo el país.' : 'Join leaders from across the country for a private cocktail reception celebrating the journalists, storytellers, and changemakers whose work is elevating Latino voices and strengthening communities nationwide.',
    dateLabel: isEs ? 'Fecha' : 'Date',
    date: isEs ? '24 de julio, 2026' : 'July 24, 2026',
    locationLabel: isEs ? 'Ubicación' : 'Location',
    location: isEs ? 'Nueva Orleans' : 'New Orleans',
    timeLabel: isEs ? 'Hora' : 'Time',
    time: '4:00 PM – 5:30 PM',
    download: isEs ? 'Descargar invitación' : 'Download Invitation',
    viewFlyer: isEs ? 'Ver invitación completa' : 'View full invitation',
    register: isEs ? 'Registrarme' : 'Register Now',
    registerHelper: isEs ? 'Se requiere confirmación. Los espacios son limitados.' : 'RSVP required. Seating is limited.'
  };
  return <div className="min-h-screen bg-gradient-to-br from-haa-navy via-haa-blue to-haa-midblue text-white font-nunito relative overflow-hidden" data-id="element-3696">
      {/* Decorative blurs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-haa-skyblue/20 rounded-full blur-3xl pointer-events-none" data-id="element-3697" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-haa-blue/30 rounded-full blur-3xl pointer-events-none" data-id="element-3698" />
      <div className="absolute top-1/3 right-0 w-80 h-80 bg-haa-midblue/20 rounded-full blur-3xl pointer-events-none" data-id="element-3699" />

      {/* Header */}
      <header className="relative z-10 w-full px-6 py-6 sm:px-12 flex items-center justify-between border-b border-white/10 backdrop-blur-sm" data-id="element-3700">
        <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association — Home" data-id="element-3701">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain brightness-0 invert group-hover:opacity-80 transition-opacity" data-id="element-3702" />
        </a>
        <a href="#" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm font-semibold transition-colors" data-id="element-3703">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-3704" />
          {t.back}
        </a>
      </header>

      {/* Main content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-20" data-id="element-3705">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-3706">
          {/* Flyer "popup" card */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.92,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="order-2 lg:order-1 flex justify-center" data-id="element-3707">
            <div className="relative w-full max-w-md" data-id="element-3708">
              {/* Soft glow */}
              <div className="absolute -inset-4 bg-haa-skyblue/30 rounded-3xl blur-2xl pointer-events-none" data-id="element-3709" />
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="relative block rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20 bg-white group" aria-label={t.viewFlyer} data-id="element-3710">
                <img src={FLYER_IMAGE} alt={t.title} className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-3711" />
              </a>
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-white text-haa-navy font-bold text-sm hover:bg-haa-skyblue hover:text-haa-navy transition-all duration-200 shadow-lg hover:shadow-xl" data-id="element-3712">
                <DownloadIcon className="w-4 h-4" data-id="element-3713" />
                {t.download}
              </a>
            </div>
          </motion.div>

          {/* Details */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.15
        }} className="order-1 lg:order-2" data-id="element-3714">
            <span className="inline-block font-nunito text-haa-skyblue text-sm uppercase tracking-[0.3em] font-bold" data-id="element-3715">
              {t.eyebrow}
            </span>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-wider leading-[0.9] mt-3 text-white" data-id="element-3716">
              {t.title}
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-xl" data-id="element-3717">
              {t.subtitle}
            </p>

            {/* Detail rows */}
            <div className="mt-10 space-y-4 max-w-xl" data-id="element-3718">
              <DetailRow icon={<CalendarIcon className="w-5 h-5" data-id="element-3720" />} label={t.dateLabel} value={t.date} data-id="element-3719" />
              <DetailRow icon={<MapPinIcon className="w-5 h-5" data-id="element-3722" />} label={t.locationLabel} value={t.location} data-id="element-3721" />
              <DetailRow icon={<ClockIcon className="w-5 h-5" data-id="element-3724" />} label={t.timeLabel} value={t.time} data-id="element-3723" />
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3" data-id="element-3725">
              <a href={REGISTRATION_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-yellow-400 text-haa-navy font-bold text-base hover:bg-yellow-300 transition-all duration-200 shadow-lg hover:shadow-xl ring-2 ring-yellow-400/40 hover:ring-yellow-400/0" data-id="element-3726">
                <CheckCircleIcon className="w-5 h-5" data-id="element-3727" />
                {t.register}
              </a>
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-white text-haa-navy font-bold text-base hover:bg-haa-skyblue transition-all duration-200 shadow-lg hover:shadow-xl" data-id="element-3728">
                {t.viewFlyer}
              </a>
              <a href="#" className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold text-base transition-all duration-200" data-id="element-3729">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-3730" />
                {t.back}
              </a>
            </div>
            <p className="mt-3 text-sm text-white/60" data-id="element-3731">{t.registerHelper}</p>
          </motion.div>
        </div>
      </main>
    </div>;
}
function DetailRow({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15" data-id="element-3732">
      <div className="flex-shrink-0 w-11 h-11 rounded-full bg-white/15 flex items-center justify-center text-haa-skyblue" data-id="element-3733">
        {icon}
      </div>
      <div data-id="element-3734">
        <p className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold" data-id="element-3735">
          {label}
        </p>
        <p className="text-lg font-bold text-white mt-0.5" data-id="element-3736">{value}</p>
      </div>
    </div>;
}